import React, { useEffect, useState } from "react";
import api from "../../api/client";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { CheckCircle2, KeyRound, Save } from "lucide-react";
import { toast } from "sonner";

export default function IntegrationsConfigTab({ config, onSaved }) {
  const [form, setForm] = useState({
    google_maps_api_key: "",
    microsoft_client_id: "",
    microsoft_tenant: "common",
    microsoft_redirect_uri: "",
    microsoft_client_secret: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm((current) => ({
      ...current,
      microsoft_client_id: config?.microsoft_client_id || "",
      microsoft_tenant: config?.microsoft_tenant || "common",
      microsoft_redirect_uri: config?.microsoft_redirect_uri || "",
      google_maps_api_key: "",
      microsoft_client_secret: "",
    }));
  }, [config]);

  const change = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/integration-config", form);
      toast.success("Credenciais de integração salvas com segurança");
      setForm((current) => ({ ...current, google_maps_api_key: "", microsoft_client_secret: "" }));
      await onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar integrações");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="integration-config-tab">
      <section className="space-y-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="label-tech">Google Maps Platform</h3>
            <Badge variant={config?.google_maps_has_key ? "secondary" : "outline"}>
              {config?.google_maps_has_key ? "Chave configurada" : "Não configurado"}
            </Badge>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Usada para geocodificação e cálculo real de distância/tempo na Agenda Inteligente. A chave nunca é enviada ao navegador.
          </p>
        </div>
        {config?.google_maps_has_key && (
          <div className="flex items-center gap-2 rounded-lg border bg-muted/20 px-3 py-2 text-sm font-mono">
            <CheckCircle2 className="size-4 text-primary" /> {config.google_maps_key_masked || "Chave protegida"}
          </div>
        )}
        <div>
          <Label>Google Maps API Key</Label>
          <Input
            className="mt-1"
            type="password"
            autoComplete="new-password"
            value={form.google_maps_api_key}
            onChange={(e) => change("google_maps_api_key", e.target.value)}
            placeholder={config?.google_maps_has_key ? "Deixe vazio para manter a chave atual" : "AIza..."}
            data-testid="google-maps-api-key-input"
          />
        </div>
      </section>

      <section className="space-y-4 border-t border-border pt-6">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="label-tech">Microsoft Graph / Outlook</h3>
            <Badge variant={config?.microsoft_client_id && config?.microsoft_has_client_secret ? "secondary" : "outline"}>
              {config?.microsoft_client_id && config?.microsoft_has_client_secret ? "App configurado" : "Configuração incompleta"}
            </Badge>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Credenciais globais do App Registration. Cada usuário conecta sua própria conta Outlook depois, em Integrações.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label>Application (Client) ID</Label>
            <Input className="mt-1" value={form.microsoft_client_id} onChange={(e) => change("microsoft_client_id", e.target.value)} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" data-testid="microsoft-client-id-input" />
          </div>
          <div>
            <Label>Tenant</Label>
            <Input className="mt-1" value={form.microsoft_tenant} onChange={(e) => change("microsoft_tenant", e.target.value)} placeholder="common" data-testid="microsoft-tenant-input" />
            <p className="mt-1 text-[11px] text-muted-foreground">Use <code>common</code> para permitir contas pessoais e corporativas, ou informe o Tenant ID.</p>
          </div>
          <div className="md:col-span-2">
            <Label>Redirect URI cadastrada no Microsoft Entra</Label>
            <Input className="mt-1 font-mono text-xs" value={form.microsoft_redirect_uri} onChange={(e) => change("microsoft_redirect_uri", e.target.value)} placeholder="https://seu-backend/api/integrations/outlook/callback" data-testid="microsoft-redirect-uri-input" />
          </div>
          <div className="md:col-span-2">
            <Label>Client Secret</Label>
            {config?.microsoft_has_client_secret && <div className="mb-2 mt-1 flex items-center gap-2 text-xs text-muted-foreground"><KeyRound className="size-3.5" /> {config.microsoft_client_secret_masked || "Segredo protegido"}</div>}
            <Input
              type="password"
              autoComplete="new-password"
              value={form.microsoft_client_secret}
              onChange={(e) => change("microsoft_client_secret", e.target.value)}
              placeholder={config?.microsoft_has_client_secret ? "Deixe vazio para manter o segredo atual" : "Valor do Client Secret"}
              data-testid="microsoft-client-secret-input"
            />
          </div>
        </div>
      </section>

      <div className="rounded-xl border border-border/70 bg-muted/15 p-4 text-xs leading-relaxed text-muted-foreground">
        Segredos são criptografados no banco com AES-256-GCM. Variáveis de ambiente continuam existindo apenas como fallback técnico; o fluxo normal de administração é por esta tela.
      </div>

      <Button onClick={save} disabled={saving} data-testid="save-integration-config-btn">
        <Save className="mr-1 size-4" /> {saving ? "Salvando…" : "Salvar APIs e integrações"}
      </Button>
    </div>
  );
}
